import { AudioWaveformIcon, Check, type LucideIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/shared/components/ui/sidebar";
import { Skeleton } from "./skeleton";
import { IWorkspace } from "@/shared/services/interface/responses/IWorkspaceResponses";
import { getDashboardLink } from "@/shared/utils/link";
import { ROUTES } from "@/core/router/routes";

export function NavMain({
  skeleton = false,
  items,
}: {
  skeleton?: boolean;
  items: {
    title: string;
    url: string;
    icon: LucideIcon;
    isActive?: boolean;
    badge?: number;
    isHide?: boolean;
    items?: {
      title: string;
      url: string;
      isWorkspace?: boolean;
      workspace?: IWorkspace;
      beforeIcon?: LucideIcon;
    }[];
  }[];
}) {
  const navigate = useNavigate();

  const dashboardUrl = "/dashboard/";
  const pathName = useLocation().pathname;

  const activeIndicator = () => {
    return (
      <div
        className="w-[2px] z-[1] bg-zinc-950 rounded-r-lg absolute h-[16px]"
        style={{ left: "0px" }}
      ></div>
    );
  };
  const activeIndicatorSubITem = () => {
    return (
      <div
        className="w-[2px] z-[1] bg-zinc-950 rounded-r-lg absolute h-[16px]"
        style={{ left: "26px" }}
      ></div>
    );
  };

  return (
    <SidebarGroup>
      <SidebarMenu>
        {!skeleton &&
          items.map((item) => {
            const itemIsListOrDashboard =
              item.url == "/dashboard/specification/list";
            const isActivePath = itemIsListOrDashboard
              ? pathName === dashboardUrl
                ? true
                : pathName.includes(items[0].url)
              : pathName.includes(item.url);

            return (
              <SidebarMenuItem key={item.title}>
                <div className="flex flex-row items-center justify-between gap-2 w-full">
                  <div
                    className="flex flex-row items-center justify-between w-full cursor-pointer mb-[4px]"
                    onClick={() => navigate(item.url)}
                  >
                    <SidebarMenuButton
                      asChild
                      tooltip={item.title}
                      hasChildren={item.items?.length ? true : false}
                      className={`h-[36px] ${
                        isActivePath
                          ? "bg-white border border-zinc-200 rounded-[8px]"
                          : ""
                      }`}
                    >
                      <div
                        className={`flex flex-row items-center justify-between w-full`}
                      >
                        {isActivePath && activeIndicator()}
                        <p
                          className="cursor-pointer flex flex-row items-center justify-start gap-2.5"
                          onClick={() => navigate(item.url)}
                        >
                          <item.icon className="w-4 h-4" color="#71717A" />
                          <span className="text-sidebar-foreground font-500 text-sm leading-5 mt-0.5 ">
                            {item.title}
                          </span>
                        </p>
                        {item?.badge != 0 && item?.badge != undefined && (
                          <div
                            onClick={() => navigate(item.url)}
                            className="cursor-pointer flex flex-row items-center justify-center gap-2 bg-zinc-100 rounded-[4px] border border-zinc-200 border-1"
                            style={{
                              width: "22px",
                              height: "20px",
                            }}
                          >
                            <span className="text-muted-foreground text-[12px] font-600">
                              {item.badge}
                            </span>
                          </div>
                        )}
                      </div>
                    </SidebarMenuButton>
                  </div>
                </div>
                {/* child items */}
                {item.items?.length ? (
                  <>
                    <SidebarMenuSub className="w-full !border-none pl-[25px]">
                      {item.items?.map((subItem) => {
                        const isActivePath = pathName === subItem.url;

                        return subItem.isWorkspace ? (
                          <>
                            <SidebarMenuSubItem key={subItem.title}>
                              <SidebarMenuSubButton
                                asChild
                                className="cursor-pointer w-full"
                                onClick={() =>
                                  navigate(
                                    getDashboardLink(
                                      ROUTES.Dashboard.Setting.Workspace
                                        .SwitchLoading
                                    ) +
                                      `?workspaceId=${subItem.workspace?.id}&returnUrl=${pathName}`
                                  )
                                }
                              >
                                <div className="flex flex-row items-center justify-between gap-2">
                                  <div className="flex flex-row items-center justify-start gap-2">
                                    <div
                                      className={`w-6 h-6 flex items-center justify-center rounded-[4px] ${
                                        subItem.workspace?.isCurrentWorkspace
                                          ? "bg-zinc-950"
                                          : "bg-[#E9E9E9]"
                                      }`}
                                    >
                                      <AudioWaveformIcon
                                        className={`w-4 h-4 ${
                                          subItem.workspace?.isCurrentWorkspace
                                            ? "text-white"
                                            : "text-zinc-950"
                                        }`}
                                      />
                                    </div>
                                    <p
                                      className="cursor-pointer truncate w-[110px] text-ellipsis"
                                      onClick={() => navigate(subItem.url)}
                                    >
                                      <span className="text-sm leading-5 text-zinc-950 ml-2">
                                        {subItem.title}
                                      </span>
                                    </p>
                                  </div>
                                  {subItem?.workspace?.isCurrentWorkspace && (
                                    <Check className="w-4 h-4 text-zinc-950" />
                                  )}
                                </div>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          </>
                        ) : (
                          <div
                            className={`flex flex-row h-[32px] z-[1] items-center justify-between w-full ${
                              isActivePath ? "bg-white " : ""
                            }`}
                          >
                            {isActivePath && activeIndicatorSubITem()}
                            {!subItem.beforeIcon && (
                              <SidebarMenuSubItem
                                key={subItem.title}
                                className="w-full h-[32px]"
                              >
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActivePath}
                                >
                                  {!subItem.beforeIcon && (
                                    <p
                                      className="cursor-pointer "
                                      onClick={() => navigate(subItem.url)}
                                    >
                                      <span>{subItem.title}</span>
                                    </p>
                                  )}
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            )}

                            {subItem.beforeIcon && (
                              <SidebarMenuSubItem
                                key={subItem.title}
                                className="w-full h-[28px]"
                              >
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActivePath}
                                >
                                  {subItem.beforeIcon && (
                                    <p
                                      className="cursor-pointer flex flex-row items-center justify-start gap-[4px]"
                                      onClick={() => navigate(subItem.url)}
                                    >
                                      <subItem.beforeIcon className="w-4 h-4" />
                                      <span className="mt-[4px]">
                                        {subItem.title}
                                      </span>
                                    </p>
                                  )}
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            )}
                          </div>
                        );
                      })}
                    </SidebarMenuSub>
                  </>
                ) : null}
              </SidebarMenuItem>
            );
          })}
        {skeleton && (
          <div className="space-y-[17px]">
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                className="flex items-center gap-2"
                key={`skeleton-${index}`}
              >
                <Skeleton
                  key={index}
                  className="w-5 h-5 bg-zinc-100 rounded-[6px]"
                />
                <Skeleton
                  key={index}
                  className="w-full h-5 bg-zinc-100 rounded-[6px]"
                />
              </div>
            ))}
            {Array.from({ length: 1 }).map((_, index) => (
              <div
                className="flex items-center gap-2"
                key={`skeleton-2-${index}`}
              >
                <Skeleton
                  key={index}
                  className="w-5 h-5 bg-zinc-100 rounded-[6px]"
                />
                <Skeleton
                  key={index}
                  className="w-2/3 h-5 bg-zinc-100 rounded-[6px]"
                />
              </div>
            ))}
            {Array.from({ length: 1 }).map((_, index) => (
              <div
                className="flex items-center gap-2"
                key={`skeleton-3-${index}`}
              >
                <Skeleton
                  key={index}
                  className="w-5 h-5 bg-zinc-100 rounded-[6px]"
                />
                <Skeleton
                  key={index}
                  className="w-1/2 h-5 bg-zinc-100 rounded-[6px]"
                />
              </div>
            ))}
          </div>
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
}
